/**
 * Rangkaian layar kasir: **PIN → sesi kas → kasir**.
 *
 * ### ⭐ Kenapa urutannya mengikat, bukan sekadar navigasi
 * Tiap tahap adalah **prasyarat** tahap berikutnya, dan prasyaratnya bukan buatan layar ini:
 * - tanpa PIN, tak ada izin POS — server menolak void, tutup sesi, dan buka laci;
 * - tanpa sesi kas, tak ada penanggung jawab uang — dan `business_date` pesanan pun diturunkan
 *   dari sesi, bukan dari jam terminal.
 *
 * ⇒ Layar tak "memaksa alur"; ia hanya menampilkan tahap yang benar-benar bisa dikerjakan.
 * Menampilkan layar kasir tanpa sesi hanya menghasilkan tombol yang pasti gagal.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from './api/client';
import { CACHE_KEYS, cacheDrop, cacheGet, cacheSet } from './api/cache';
import { businessDateFromRule } from '@osbpos/contract';
import type { BusinessDateRule } from '@osbpos/contract';
import { devicePairing, staffSession, storedToken } from './api/session';
import { CashierShell } from './shell/CashierShell';
import { PairDevice } from './shell/PairDevice';
import { KitchenShell } from './shell/KitchenShell';
import { QueueShell } from './shell/QueueShell';
import { WaiterShell } from './shell/WaiterShell';
import { PinLogin } from './shell/PinLogin';
import { SessionShell } from './shell/SessionShell';
import type { MenuItem } from './api/client';

/** Bentuk layar yang benar-benar ada di `pos-web`. ⚠️ Cerminan `POS_SHELLS` di kontrak. */
type Shell = 'cashier' | 'kds' | 'queue' | 'waiter';

export interface AppProps {
  readonly outletCode: string;
  /**
   * Peran perangkat ini (K4). ⭐ `kds` = layar dapur: butuh PIN, **tapi tak butuh sesi kas** —
   * juru masak tak memegang uang, dan menuntut sesi kas di sana berarti dapur tak bisa menyala
   * sampai ada kasir yang membuka laci.
   *
   * ⚠️ **Suntikan uji / override pengembangan saja.** Di produksi perannya datang dari SERVER
   * (`GET /devices/me`, dari `sub` token) — lihat komentar `shell` di bawah.
   */
  readonly role?: 'cashier' | 'kds' | 'queue' | 'waiter';
  readonly deviceCode: string;
  /**
   * ⚠️ **Nilai suntikan uji saja** — di produksi ia tak dikirim, dan tanggal usaha datang dari
   * SERVER (`GET /outlets/:code/business-date`). Lihat komentar `businessDate` di bawah.
   */
  readonly businessDate?: string;
  readonly menu?: readonly MenuItem[];
  /** Sesi kas awal (uji). Di produksi didapat dari layar buka-sesi. */
  readonly shiftId?: string | null;
}

/**
 * Jawaban `GET /devices/me` sebagaimana disalin perangkat.
 *
 * ⚠️ Sebelum 2026-08-06 salinan ini cuma `Shell` telanjang. Pembacanya menoleransi bentuk lama
 * (lihat `identitasTersimpan`) supaya tablet yang sudah jalan tak kehilangan shell-nya saat
 * bundel baru mendarat — kehilangan salinan berarti tablet luring berhenti di "belum dikenali".
 */
interface Identity {
  readonly shell: Shell | null;
  readonly deviceCode: string;
  readonly outletCode: string;
  /** ⭐ Tier penempatan (K13) — ikut disalin supaya tablet luring tetap tahu modenya. */
  readonly tier?: 'server' | 'tablet';
  /**
   * ⭐⭐ Profil printer perangkat ini — ikut DISALIN supaya laci tetap bisa ditendang saat luring.
   * ⚠️ Tanpa salinan ini, tepat pada keadaan yang paling butuh (jaringan mati, tamu menunggu
   * kembalian) kasir justru kehilangan lacinya.
   */
  readonly printer?: unknown;
}

/**
 * Membaca salinan identitas, **termasuk bentuk lamanya**.
 *
 * ⛔ Sebelum 2026-08-06 yang tersimpan cuma `Shell` telanjang (`"cashier"`). Membacanya sebagai
 * objek menghasilkan `undefined` di ketiga bidang ⇒ tablet yang boot LURING sesudah bundel baru
 * mendarat berhenti di "perangkat belum dikenali", padahal ia punya salinan yang sah. Kompatibilitas
 * ini boleh dibuang setelah semua tablet pernah daring sekali dengan bundel baru.
 */
function identitasTersimpan(): Partial<Identity> {
  const v = cacheGet<Identity | Shell | null>(CACHE_KEYS.whoAmI)?.value;
  if (v === undefined) return {};
  if (v === null || typeof v === 'string') return { shell: v };
  return v;
}

export function App(p: AppProps) {
  const { t } = useTranslation();
  const [loggedIn, setLoggedIn] = useState(() => staffSession() !== undefined);
  /**
   * Sudah dipasangkan? ⚠️ Dibaca SEKALI saat boot — `PairDevice` memuat ulang halaman sesudah
   * berhasil.
   *
   * ⛔⛔ **Rahasia perangkat ikut dihitung**, bukan cuma tokennya. Token berumur 10 jam dan memang
   * akan habis; yang menyatakan perangkat ini sudah dipasangkan adalah rahasianya. Tanpa ini,
   * tablet yang tokennya kedaluwarsa semalam menyambut kasir dengan layar PEMASANGAN — dan
   * manajernya menerbitkan kode baru untuk perangkat yang sebenarnya tak pernah lepas.
   * (Ketahuan dari uji di server live: token dibuang, `tokenDariRahasia` berhasil menukar token
   * baru di latar, tapi layar sudah telanjur memutuskan "belum dipasangkan".)
   */
  const [bertoken] = useState(() => storedToken() !== undefined || devicePairing() !== undefined);
  /**
   * ⭐⭐⭐ **Token MATI dan tak bisa diperbarui ⇒ kembali ke PINTU, bukan ke jalan buntu.**
   *
   * ⛔ Ini keadaan yang benar-benar terjadi (Imron, 2026-08-06): peramban masih menyimpan token
   * lama dari langkah runbook — sudah kedaluwarsa — tapi belum pernah dipasangkan, jadi tak ada
   * rahasia perangkat untuk menukarnya. Layar berhenti di *"perangkat belum dikenali"*, kalimat
   * yang benar tapi **tak bisa ditindaklanjuti siapa pun**: tak ada tombol, tak ada jalan keluar,
   * dan satu-satunya obatnya membuka DevTools — persis yang pemasangan perangkat ada untuk
   * menghapusnya.
   *
   * ⚠️ Dideteksi dari FAKTA, bukan dari tebakan: penangan 401 di `client.ts` sudah membuang
   * tokennya (`lupakanToken`). Jadi kalau sesudah `whoAmI` gagal tak ada token DAN tak ada rahasia,
   * perangkat ini memang tak punya cara apa pun untuk membuktikan dirinya.
   *
   * ⛔ Yang sengaja TIDAK ikut terjaring: gagal karena **jaringan**. `fetch` yang menolak tak
   * pernah memanggil `lupakanToken`, jadi tokennya masih ada — dan tablet luring tetap memakai
   * salinannya (OFF1–OFF4), bukan diseret ke layar pemasangan yang justru butuh internet.
   */
  const [perluPasang, setPerluPasang] = useState(false);
  /**
   * ⭐⭐⭐ **Kode perangkat & outlet datang dari SERVER juga**, bukan dari `localStorage`.
   *
   * K21 memindahkan BENTUK layar ke `GET /devices/me` tapi meninggalkan **kode**-nya di peramban —
   * separuh perpindahan, dan separuhnya yang tertinggal justru yang dipakai untuk membuka sesi kas
   * dan menandai pesanan. Akibatnya nyata dan langsung: terminal yang tokennya `device:POS-SMB-01`
   * mengaku `POS-DEV-01` (nilai bawaan `main.tsx`) dan ditolak `perangkat tak terdaftar` — gejala
   * yang menunjuk ke pendaftaran perangkat, padahal yang salah adalah dari mana kodenya diambil.
   *
   * ⛔ Dan yang lebih dalam: kode perangkat adalah **identitas**, dan identitas tak boleh datang
   * dari argumen yang bisa diketik siapa pun — sumbu yang sama dengan R-AI-1 untuk tenant. Server
   * kini menolak ketidakcocokannya (`AUTH-403-DEVICE_MISMATCH`); yang di sini memastikan layar tak
   * pernah mengirim yang salah sejak awal.
   *
   * ⚠️ Prop-nya tetap ada sebagai nilai awal — uji e2e menyuntiknya, dan tablet yang boot luring
   * memakai salinan terakhir sampai server menjawab.
   */
  const [deviceCode, setDeviceCode] = useState<string>(
    identitasTersimpan().deviceCode ?? p.deviceCode,
  );
  const [outletCode, setOutletCode] = useState<string>(
    identitasTersimpan().outletCode ?? p.outletCode,
  );

  /*
   * ⭐⭐ **Sesi kas yang sedang berjalan ikut disalin** — kalau tidak, login PIN luring cuma pintu
   * yang membuka ke tembok: kasir masuk, lalu berhenti di layar "buka sesi kas" yang tak bisa
   * dikirim ke mana pun.
   *
   * ⛔ Salinan ini **petunjuk, bukan kebenaran**. Begitu server terjangkau, jawabannyalah yang
   * berlaku — termasuk bila sesinya ternyata sudah ditutup dari tempat lain.
   */
  const dariSalinan = useRef(false);
  const [shiftId, setShiftId] = useState<string | null>(() => {
    if (p.shiftId !== undefined && p.shiftId !== null) return p.shiftId;
    const simpan = cacheGet<string>(CACHE_KEYS.shift(outletCode, deviceCode))?.value;
    if (simpan === undefined) return null;
    dariSalinan.current = true;
    return simpan;
  });

  /*
   * ⚠️ Hanya dijalankan bila nilainya datang dari SALINAN. Kalau tak ada salinan, `SessionShell`
   * yang bertanya — menanyakannya dua kali cuma menambah permintaan tanpa menambah jawaban.
   */
  useEffect(() => {
    if (!dariSalinan.current) return;
    let alive = true;
    void api
      .currentShift(outletCode, deviceCode)
      .then((r) => {
        if (!alive) return;
        /*
         * ⭐⭐⭐ **Tandanya dilepas SESUDAH jawabannya dipakai, bukan sebelum permintaan dikirim.**
         *
         * ⛔⛔ Versi lama melepasnya di baris pertama efek. Akibatnya begitu komponen ini
         * dipasang-ulang — StrictMode dev hari ini, tapi juga error-boundary yang mencoba lagi,
         * `key` yang berganti, atau tab yang dipulihkan — pemasangan PERTAMA sudah melepas
         * tandanya dan `alive=false` membuang jawabannya, sementara pemasangan KEDUA pulang
         * lebih awal karena tandanya sudah hilang. Pemeriksaan basi-tidaknya sesi kas jadi
         * **tak pernah terjadi sama sekali**, tanpa satu pun gejala.
         *
         * ⚠️ Terukur di UAT 2026-08-26: di bundel dev, till yang sesinya BARU SAJA ditutup lalu
         * dimuat ulang tetap mendarat di layar kasir dan menyelesaikan penjualan tunai sampai
         * "Lunas" — mendarat dengan `shift_id = NULL`, di luar sesi yang uangnya baru dihitung.
         * ⭐ Bundel PRODUKSI benar (React tak menjalankan efek dua kali di luar dev), jadi ini
         * bukan cacat yang pernah sampai ke pengguna — tapi yang menahannya cuma satu perilaku
         * React yang kebetulan berbeda di dev, dan itu bukan penjaga.
         */
        dariSalinan.current = false;
        if (r.shiftId === undefined) {
          // ⛔ Server bilang tak ada sesi terbuka ⇒ salinannya SALAH dan harus mati, bukan dipakai.
          cacheDrop(CACHE_KEYS.shift(outletCode, deviceCode));
          setShiftId(null);
          /* ⛔ Tanggal sesi ikut dilepas: sesi yang sudah tutup tak boleh terus menjangkarkan
             tanggal, kalau tidak terminal itu berjualan di hari kemarin sampai dimuat ulang. */
          setShiftDate(null);
          return;
        }
        setShiftId(r.shiftId);
        if (r.businessDate !== undefined) setShiftDate(r.businessDate);
      })
      /* Tak terjangkau ⇒ salinannya tetap dipakai. Itulah gunanya ia ada. */
      .catch(() => undefined);
    return () => { alive = false; };
  }, [outletCode, deviceCode]);
  /**
   * ⭐⭐ Tanggal usaha dari **SERVER**, lalu dari **SESI KAS** — tak pernah dari jam perangkat.
   *
   * ⛔ Jam dinding di layar bayar adalah kebohongan kecil yang menuntun ke sengketa besar: sesi
   * yang dibuka kemarin sore masih berjalan pukul 01:30, dan transaksinya milik hari KEMARIN (§2).
   * Kasir yang membaca tanggal hari ini di layar akan menandatangani setoran hari yang salah.
   *
   * ⛔⛔ Dan perangkat memang **tak bisa** menjawabnya sendiri, sekalipun jamnya benar: tanggal
   * usaha adalah `day_cutover_time` + zona waktu OUTLET, dua setelan yang hidup di database.
   * Versi lama memakai `new Date().toISOString().slice(0,10)` — itu tanggal **UTC**, yang di
   * Indonesia (UTC+7) meleset satu hari setiap pukul 00:00–07:00 waktu setempat. Sesi kas yang
   * dibuka pukul 06:00 mendarat di hari KEMARIN, lengkap dengan seluruh pesanannya.
   *
   * ⚠️ Gejalanya jahat: benar sepanjang jam kerja, salah hanya pagi buta — jadi yang menemukannya
   * adalah outlet yang buka pagi, dan siapa pun yang mencoba mengulanginya siang hari tak bisa.
   *
   * ⚠️ Nuansa yang sengaja TIDAK dimodelkan sebagai dua state terpisah: "hari usaha sekarang"
   * (untuk membuka sesi) dan "hari usaha SESI ini" (untuk pesanan) berbeda hanya saat sebuah sesi
   * masih terbuka melewati cutover — dan di keadaan itu settlement memang sudah ditolak server
   * karena ada sesi terbuka. Yang dari sesi menimpa yang dari server lewat `onSessionChanged`.
   */
  /**
   * ⛔⛔⛔ **DUA sumber, dan yang menang harus PASTI — bukan siapa yang jawabannya datang belakangan.**
   *
   * Sampai 2026-08-23 keduanya menulis satu state yang sama:
   *   1. `GET /outlets/:code/business-date` → tanggal usaha SERVER **saat ini**;
   *   2. `GET /outlets/:code/shifts/current` → tanggal usaha **SESI KAS** yang sedang terbuka.
   *
   * Selama keduanya sama, lombanya tak terlihat. Ia jadi terlihat tepat pada keadaan yang paling
   * sering di lapangan: **sesi dibuka kemarin sore dan belum ditutup ketika jam pergantian hari
   * lewat**. Sesi `TB_ALYSIA/2026-08-22` masih terbuka pada tanggal 23; kalau jawaban (1) mendarat
   * belakangan, seluruh layar tiba-tiba memakai tanggal 23 — dan daftar TERTAHAN, yang menanyai
   * server dengan tanggal itu, mengembalikan NOL. Kasir menekan Tahan, billnya tersimpan (server
   * menjawab 201), lalu **tak muncul di mana pun**. Persis yang dilaporkan.
   *
   * ⇒ Disimpan TERPISAH, dan yang berlaku dipilih dengan aturan tetap: **sesi kas menang**.
   * Transaksi milik hari SESInya (§2) — itu sebabnya sesi punya `business_date` sendiri sejak awal.
   * ⚠️ Tanggal server tetap dipakai saat belum ada sesi (pelayan, KDS, papan antrean, dan kasir
   * sebelum sesi dibuka) — di sana memang tak ada yang menjangkarkannya.
   */
  const [serverDate, setServerDate] = useState<string | null>(p.businessDate ?? null);
  const [shiftDate, setShiftDate] = useState<string | null>(null);
  const businessDate = shiftDate ?? serverDate;
  const [toSession, setToSession] = useState(false);

  /**
   * ⭐⭐⭐ **Bentuk layar datang dari PENDAFTARAN perangkat, bukan dari peramban.**
   *
   * K4 sudah menyatakannya sejak awal — *"yang menentukan bentuk layar adalah PERANGKATnya, bukan
   * orangnya"* — tapi sampai sekarang yang benar-benar menentukan adalah `localStorage`. Artinya
   * `device.role` yang disetel manajer di portal **tak menentukan apa pun**, dan siapa pun yang
   * membuka DevTools bisa mengubah tablet dapur jadi terminal kasir.
   *
   * ⛔ `undefined` = belum dijawab (jangan render apa pun yang salah); `null` = perannya memang
   * **belum punya layar** (kiosk, ringkasan dapur, agen cetak) ⇒ katakan, jangan jatuhkan ke kasir.
   * Tablet ringkasan dapur yang diam-diam menampilkan layar bayar mengundang orang menerima uang
   * di mesin yang bukan till.
   *
   * ⚠️ `p.role` tetap menang bila diberikan — itu jalur SUNTIKAN UJI, dan `?role=` di produksi
   * kini hanya berlaku saat `import.meta.env.DEV` (lihat `main.tsx`).
   */
  const [shell, setShell] = useState<Shell | null | undefined>(
    /*
     * ⭐ Salinan JAWABAN SERVER yang terakhir — bukan `localStorage.osbpos.role` yang lama. Peran
     * perangkat hampir tak pernah berubah, jadi salinan ini benar hampir selalu; dan kalau ia
     * berubah, penyegaran di bawah memperbaikinya dalam satu putaran.
     */
    p.role ?? identitasTersimpan().shell,
  );
  /**
   * ⭐⭐⭐ **Tier penempatan — dari SERVER** (K13, dipindah dari `localStorage` 2026-08-23).
   *
   * ⛔ Sampai kini ia `localStorage.osbpos.tier`: manajer tak bisa menyetelnya, HQ tak bisa
   * melihatnya, dan siapa pun yang membuka DevTools bisa mengubah **dari mana angka bill datang**
   * — kesalahan yang sama persis dengan `role` sebelum K21, dengan akibat yang lebih besar.
   *
   * ⚠️ Bawaan `server`: yang belum dijawab server diperlakukan sebagai perilaku yang sudah
   * berjalan, bukan mode hitung-sendiri.
   */
  const [tier, setTier] = useState<'server' | 'tablet'>(
    identitasTersimpan().tier ?? 'server',
  );
  /* ⭐ Salinan profil printer — dipakai layar kasir untuk menendang laci sendiri (lihat K27). */
  const [printer, setPrinter] = useState<unknown>(identitasTersimpan().printer ?? null);
  useEffect(() => {
    if (p.role !== undefined) return;
    let alive = true;
    void api
      .whoAmI()
      .then((d) => {
        if (!alive) return;
        setShell(d.shell);
        /* ⭐ Kode perangkat & outlet ikut — jawaban server yang berlaku, bukan isi peramban. */
        setDeviceCode(d.deviceCode);
        setOutletCode(d.outletCode);
        setTier(d.tier ?? 'server');
        setPrinter(d.printer ?? null);
        cacheSet<Identity>(CACHE_KEYS.whoAmI, {
          shell: d.shell, deviceCode: d.deviceCode, outletCode: d.outletCode,
          tier: d.tier ?? 'server',
          ...(d.printer === undefined || d.printer === null ? {} : { printer: d.printer }),
        });
      })
      /*
       * ⚠️ Gagal bertanya ≠ "tak punya layar". Yang pertama sementara, yang kedua keputusan.
       * ⛔ Dan gagal bertanya TIDAK menghapus salinan: tablet yang menyala luring harus tetap
       * memakai shell terakhir yang pernah dijawab server.
       */
      .catch(() => {
        if (!alive) return;
        if (storedToken() === undefined && devicePairing() === undefined) setPerluPasang(true);
      });
    return () => { alive = false; };
  }, [p.role]);

  useEffect(() => {
    let alive = true;
    void api
      .businessDate(outletCode)
      .then((d) => {
        if (!alive) return;
        setServerDate(d.businessDate);
        /*
         * ⭐⭐⭐ Yang disimpan **ATURANNYA**, bukan jawabannya. Tanggal berganti tiap hari, jadi
         * menyimpan "2026-08-06" berarti tablet yang menyala besok memakai tanggal kemarin — cache
         * yang lebih berbahaya daripada tak ada cache. Zona + jam pergantian hari tak berubah, dan
         * dengan keduanya tablet menghitung jawaban yang SAMA PERSIS dengan Postgres.
         */
        if (d.timezone !== undefined && d.cutoverTime !== undefined) {
          cacheSet<BusinessDateRule>(CACHE_KEYS.businessDateRule(outletCode), {
            timezone: d.timezone, cutoverTime: d.cutoverTime,
          });
        }
      })
      .catch(() => {
        /*
         * ⛔ LURING: hitung dari aturan yang tersimpan. ⚠️ `dayClosed` sengaja TIDAK ditebak — ia
         * lahir dari settlement, dan "belum tahu" bukan "belum ditutup". Yang menahannya adalah
         * server saat penjualan menyusul (`assertDayNotClosed`).
         */
        if (!alive) return;
        const rule = cacheGet<BusinessDateRule>(CACHE_KEYS.businessDateRule(outletCode))?.value;
        if (rule !== undefined) setServerDate(businessDateFromRule(rule));
      });
    return () => { alive = false; };
  }, [outletCode]);

  /*
   * ⭐⭐ Papan antrean bercabang **PALING DULU — sebelum PIN sekalipun.**
   *
   * ⛔ Tak ada manusia di depan layar ini untuk memasukkan PIN, dan tak akan pernah ada. Menaruhnya
   * di belakang gerbang PIN berarti papan yang mati semalam tak pernah menyala lagi sampai seseorang
   * naik kursi dan mengetik PIN di layar yang menghadap tamu. Ia memakai token PERANGKAT, dan yang
   * ditampilkannya memang tak rahasia: nomor, tanpa harga, tanpa isi pesanan.
   */
  /*
   * ⛔⛔ Belum dijawab / peran tanpa layar — DIKATAKAN, bukan dijatuhkan ke kasir.
   *
   * `undefined` sementara (server belum menjawab); `null` keputusan (peran ini memang belum punya
   * layar: kiosk, ringkasan dapur, agen cetak). Keduanya sama-sama bukan alasan menampilkan layar
   * bayar — tablet yang salah peran lalu menampilkan tombol Bayar mengundang orang menerima uang
   * di mesin yang bukan till.
   */
  /*
   * ⭐⭐⭐ **Belum punya token sama sekali ⇒ layar PEMASANGAN, bukan pesan galat.**
   *
   * ⛔ Sebelum ini keadaan ini berakhir di "perangkat belum dikenali" — kalimat yang benar tapi tak
   * bisa ditindaklanjuti siapa pun yang berdiri di depan tablet. Tablet baru memang belum dikenal;
   * yang dibutuhkannya bukan penjelasan melainkan **pintu**.
   *
   * ⚠️ Diperiksa SEBELUM `shell`, karena tanpa token `GET /devices/me` tak akan pernah menjawab.
   */
  if (!bertoken || perluPasang) {
    return <PairDevice onPaired={() => { window.location.reload(); }} />;
  }

  if (shell === undefined) return <NoBusinessDate text={t('common.deviceUnknown')} />;
  if (shell === null) return <NoBusinessDate text={t('common.deviceNoShell')} />;

  if (shell === 'queue') {
    if (businessDate === null) return <NoBusinessDate text={t('common.noBusinessDate')} />;
    return <QueueShell outletCode={outletCode} businessDate={businessDate} />;
  }

  if (!loggedIn) {
    return (
      <PinLogin
        outletCode={outletCode}
        deviceCode={deviceCode}
        onLogin={() => { setLoggedIn(true); }}
      />
    );
  }

  /*
   * ⭐⭐ Dapur bercabang di sini — **sesudah PIN, SEBELUM sesi kas**, dan urutan itu yang penting.
   *
   * ⛔ Juru masak tak memegang uang. Menuntut sesi kas untuk menyalakan layar dapur berarti dapur
   * tak bisa mulai bekerja sampai ada kasir yang membuka laci — dan di banyak outlet, dapur
   * menyala lebih dulu. PIN tetap dituntut: yang mem-bump tiket harus punya nama.
   */
  if (shell === 'kds') {
    return <KitchenShell outletCode={outletCode} onLoggedOut={() => { setLoggedIn(false); }} />;
  }

  /*
   * ⭐⭐ Pelayan juga bercabang **sesudah PIN, sebelum sesi kas** — dan alasannya sama kuatnya
   * dengan dapur: pelayan tak memegang uang. Menuntutnya membuka sesi kas berarti tiap pelayan
   * memegang laci yang tak pernah ia sentuh, dan setoran akhir hari jadi milik orang yang salah.
   *
   * ⛔ PIN tetap wajib: "siapa yang mengambil pesanan meja 7" harus punya jawaban.
   */
  if (shell === 'waiter') {
    return (
      <WaiterShell
        outletCode={outletCode}
        deviceCode={deviceCode}
        onLoggedOut={() => { setLoggedIn(false); }}
        {...(p.menu === undefined ? {} : { menu: p.menu })}
      />
    );
  }

  /*
   * ⛔⛔ Tanpa tanggal usaha dari server, TAK ADA yang boleh dibuka — bukan sesi kas, bukan
   * pesanan. Ini gerbang, bukan layar muat: menebak tanggalnya di sini berarti seluruh hari
   * mendarat di hari yang salah, dan yang menemukannya baru tutup buku.
   */
  if (businessDate === null) return <NoBusinessDate text={t('common.noBusinessDate')} />;

  if (shiftId === null || toSession) {
    return (
      <SessionShell
        outletCode={outletCode}
        deviceCode={deviceCode}
        businessDate={businessDate}
        shiftId={shiftId}
        /* ⭐ Jalan pulang ke layar kasir tanpa menyentuh apa pun — layar sesi tak boleh jadi
           ruangan tanpa pintu. ⚠️ Hanya bila kasirnya memang ada di belakangnya (staf sudah
           login); kalau tidak, "kembali" tak menuju ke mana-mana. */
        {...(loggedIn ? { onCancel: () => { setToSession(false); } } : {})}
        onSessionChanged={(s, bd) => {
          setShiftId(s);
          /* ⭐ Sesi ditutup ⇒ salinannya ikut mati. Salinan sesi mati adalah pintu yang tak
             pernah menutup: tablet yang reboot besok akan mengira harinya masih berjalan. */
          if (s === null) cacheDrop(CACHE_KEYS.shift(outletCode, deviceCode));
          else cacheSet(CACHE_KEYS.shift(outletCode, deviceCode), s);
          /* ⭐ Sesi BARU dibuka ⇒ tanggalnya menjangkarkan layar; sesi ditutup ⇒ jangkarnya
             dilepas dan tanggal SERVER kembali berlaku. Dua arah, satu state — supaya tak ada
             lagi dua sumber yang berebut menulis satu kebenaran. */
          setShiftDate(s === null ? null : (bd ?? null));
          setToSession(false);
          // Sesi ditutup DAN staf keluar ⇒ kembali ke PIN. Terminal yang tetap "masuk" setelah
          // sesi ditutup adalah terminal yang dipakai orang berikutnya atas nama orang sebelumnya.
          if (s === null && staffSession() === undefined) setLoggedIn(false);
        }}
      />
    );
  }

  /*
   * ⭐⭐ **Satu shell, bukan dua layar** (Imron, 2026-08-02).
   *
   * Dulu dashboard meja adalah LAYAR TERSENDIRI yang menggantikan layar kasir. Akibatnya seluruh
   * chrome digandakan (pita atas, panggilan tamu, tema) dan berpindah meja berarti berpindah
   * layar — dengan bill yang lenyap dari pandangan tiap kali.
   *
   * ⇒ Meja kini **isi dari container yang sama** dengan menu, dengan area sebagai railnya persis
   *   seperti departemen. Yang berganti cuma isinya, bukan layarnya.
   */
  return (
    <CashierShell
      outletCode={outletCode}
      deviceCode={deviceCode}
      shiftId={shiftId}
      businessDate={businessDate}
      tier={tier}
      printer={printer}
      onSession={() => { setToSession(true); }}
      /* ⚠️ Sesi KAS tetap terbuka — yang berganti orangnya, bukan lacinya. */
      onLoggedOut={() => { setLoggedIn(false); }}
      {...(p.menu === undefined ? {} : { menu: p.menu })}
    />
  );
}

/** ⚠️ Bukan spinner: keadaan ini bisa bertahan (server tak terjangkau) dan harus bisa dibaca. */
function NoBusinessDate({ text }: { readonly text: string }) {
  return <p className="error" data-testid="app-no-business-date">{text}</p>;
}
